import { useEffect, useState } from 'react'
import {
    Button, Col, Form, Modal, ProgressBar, Row, Spinner, Tabs, Tab
} from 'react-bootstrap'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import {
    CREATE_AUTOMATION,
    UPDATE_AUTOMATION,
    CREATE_MESSAGE_TEMPLATE,
} from '../../../gql/mutations/campaigns'
import { GET_MESSAGE_TEMPLATES } from '../../../gql/queries/campaigns'
import { Automation, IMessageTemplate, CampaignChannel } from '../../../types/campaigns'
import { TemplateStarterModal } from '../../templates/components/TemplateStarterModal'
import { TemplateFormModal } from '../../campaigns/components/TemplateFormModal'
import { TemplateStarter } from '../../../constants/templateStarters'

interface Props {
    show: boolean
    onHide: () => void
    onSaved: () => void
    automation?: Automation | null
}

const TRIGGER_OPTIONS = [
    { value: 'HAPPY_BIRTHDAY', label: '🎂 Happy Birthday', hasConfig: false },
    { value: 'CONDITIONAL_LEAD', label: '🎯 Conditional Action (Leads)', hasConfig: true },
    { value: 'CONDITIONAL_POLICY', label: '🛡️ Conditional Action (Policy/Quote)', hasConfig: true }
]

export const LEAD_CONDITIONS = [
    { value: 'LEAD_DURATION', label: 'Lead Duration' },
    { value: 'LEAD_STAGE', label: 'Basic Lead Stage (New, Sold, Completed)' },
    { value: 'LEAD_STAGE_CHANGE', label: 'Lead Stage Change' }
]

export const POLICY_CONDITIONS = [
    { value: 'STATE_CHANGE', label: 'State Change + duration in that state' },
    { value: 'CROSS_SELL_AUTO', label: 'Has Auto without Auto Policy + policy duration' },
    { value: 'CROSS_SELL_HOME', label: 'Has Home without Home Policy + policy duration' },
    { value: 'POLICY_COMPLETED', label: 'Completed Policy + duration in that state' },
    { value: 'BY_CARRIER', label: 'By Carrier (Select Carrier)' }
]

interface WizardState {
    // Step 1
    triggerType: string
    triggerConfig: Record<string, any>
    // Step 2
    channel: string
    templateId: string
    // Step 3
    name: string
    description: string
    scope: 'ORGANIZATION' | 'PERSONAL'
    delayDays: number
    sendHour: number
    isActive: boolean
}

const DEFAULT_STATE: WizardState = {
    triggerType: 'HAPPY_BIRTHDAY',
    triggerConfig: {},
    channel: 'EMAIL',
    templateId: '',
    name: '',
    description: '',
    scope: 'PERSONAL',
    delayDays: 0,
    sendHour: 9,
    isActive: true,
}

export const AutomationWizard = ({ show, onHide, onSaved, automation }: Props) => {
    const [step, setStep] = useState(1)
    const [form, setForm] = useState<WizardState>(DEFAULT_STATE)

    const [starterModalOpen, setStarterModalOpen] = useState(false)
    const [templateModalOpen, setTemplateModalOpen] = useState(false)
    const [selectedStarter, setSelectedStarter] = useState<TemplateStarter | null>(null)
    const [defaultChannel, setDefaultChannel] = useState<CampaignChannel | undefined>(undefined)
    const [activeTab, setActiveTab] = useState('general')

    const { data: templatesData, loading: loadingTemplates, refetch: refetchTemplates } = useQuery<{
        messageTemplates: IMessageTemplate[]
    }>(GET_MESSAGE_TEMPLATES, {
        skip: !show,
        fetchPolicy: 'cache-and-network',
    })

    const [createAutomation, { loading: creating }] = useMutation(CREATE_AUTOMATION)
    const [updateAutomation, { loading: updating }] = useMutation(UPDATE_AUTOMATION)
    const [createTemplateMutation, { loading: creatingTemplate }] = useMutation(CREATE_MESSAGE_TEMPLATE)

    useEffect(() => {
        if (show) {
            if (automation) {
                setForm({
                    triggerType: automation.triggerType,
                    triggerConfig: automation.triggerConfig || {},
                    channel: automation.channel,
                    templateId: automation.template?.id ?? '',
                    name: automation.name,
                    description: automation.description || '',
                    scope: automation.scope || 'PERSONAL',
                    delayDays: automation.delayDays,
                    sendHour: automation.sendHour || 9,
                    isActive: automation.isActive,
                })
                if (['HAPPY_BIRTHDAY'].includes(automation.triggerType)) {
                    setActiveTab('general')
                } else if (['CONDITIONAL_LEAD'].includes(automation.triggerType)) {
                    setActiveTab('leads')
                } else if (['CONDITIONAL_POLICY'].includes(automation.triggerType)) {
                    setActiveTab('policy')
                } else {
                    setActiveTab('general')
                }
            } else {
                setForm(DEFAULT_STATE)
                setActiveTab('general')
            }
            setStep(1)
        }
    }, [show, automation])

    const templates = (templatesData?.messageTemplates ?? []).filter(
        (t) => !form.channel || (t.channel as string) === form.channel || (t as any).allChannels?.includes(form.channel)
    )

    const set = (key: keyof WizardState, value: any) =>
        setForm((prev) => ({ ...prev, [key]: value }))

    const selectedTrigger = TRIGGER_OPTIONS.find((t) => t.value === form.triggerType)

    const handleOpenCreateTemplate = () => {
        const chan = (form.channel as CampaignChannel) || undefined
        setDefaultChannel(chan)
        setStarterModalOpen(true)
    }

    const handleSelectStarter = (starter: TemplateStarter) => {
        setSelectedStarter(starter)
        setDefaultChannel(starter.channel)
        setStarterModalOpen(false)
        setTemplateModalOpen(true)
    }

    const handleStartFromScratch = () => {
        setSelectedStarter(null)
        setStarterModalOpen(false)
        setTemplateModalOpen(true)
    }

    const handleCreateTemplateSubmit = async (payload: any) => {
        try {
            const { data } = await createTemplateMutation({
                variables: payload,
            })
            if (data?.createMessageTemplate?.ok) {
                toast.success('Template created')
                setTemplateModalOpen(false)
                setSelectedStarter(null)
                await refetchTemplates()
                if (data.createMessageTemplate.template?.id) {
                    set('templateId', data.createMessageTemplate.template.id)
                }
            } else {
                toast.error(data?.createMessageTemplate?.errors?.[0] || 'Error creating template')
            }
        } catch (error) {
            toast.error('Unable to save template')
        }
    }

    const handleSave = async () => {
        if (!form.name.trim()) {
            toast.error('Enter a name for the automation')
            return
        }
        const variables = {
            name: form.name.trim(),
            description: form.description,
            scope: form.scope,
            triggerType: form.triggerType,
            triggerConfig: form.triggerConfig,
            channel: form.channel,
            templateId: form.templateId || null,
            delayDays: form.delayDays,
            sendHour: form.sendHour,
            isActive: form.isActive,
        }

        try {
            if (automation) {
                const { data } = await updateAutomation({ variables: { id: automation.id, ...variables } })
                if (!data?.updateAutomation?.ok) throw new Error(data?.updateAutomation?.errors?.[0])
                toast.success('Automation updated')
            } else {
                const { data } = await createAutomation({ variables })
                if (!data?.createAutomation?.ok) throw new Error(data?.createAutomation?.errors?.[0])
                toast.success('Automation created')
            }
            onSaved()
            onHide()
        } catch (err: any) {
            toast.error(err?.message || 'Error saving automation')
        }
    }

    const progress = ((step - 1) / 2) * 100

    return (
        <>
        <Modal show={show} onHide={onHide} size='lg' centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {automation ? '✏️ Edit Automation' : '⚡ New Automation'}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* Progress */}
                <div className='mb-4'>
                    <div className='d-flex justify-content-between small text-muted mb-2'>
                        <span className={step === 1 ? 'fw-bold text-primary' : ''}>1. Trigger</span>
                        <span className={step === 2 ? 'fw-bold text-primary' : ''}>2. Channel + Template</span>
                        <span className={step === 3 ? 'fw-bold text-primary' : ''}>3. Configuration</span>
                    </div>
                    <ProgressBar now={progress} style={{ height: 4 }} />
                </div>

                {/* ─── Step 1: Trigger ─── */}
                {step === 1 && (
                    <div>
                        <h6 className='mb-3'>🎯 What event triggers this automation?</h6>
                        
                        <Tabs
                            activeKey={activeTab}
                            onSelect={(k) => setActiveTab(k || 'general')}
                            className="mb-3"
                        >
                            <Tab eventKey="general" title="General">
                                <Row xs={1} sm={2} className='g-2'>
                                    {TRIGGER_OPTIONS.filter((t) => ['HAPPY_BIRTHDAY'].includes(t.value)).map((trigger) => (
                                        <Col key={trigger.value}>
                                            <div
                                                className={`border rounded p-3 cursor-pointer h-100 ${form.triggerType === trigger.value
                                                    ? 'border-primary bg-primary bg-opacity-10'
                                                    : 'hover-shadow'
                                                    }`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => set('triggerType', trigger.value)}
                                            >
                                                <div className='fw-semibold'>{trigger.label}</div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Tab>
                            <Tab eventKey="leads" title="Leads">
                                <Row xs={1} sm={2} className='g-2'>
                                    {TRIGGER_OPTIONS.filter((t) => ['CONDITIONAL_LEAD'].includes(t.value)).map((trigger) => (
                                        <Col key={trigger.value}>
                                            <div
                                                className={`border rounded p-3 cursor-pointer h-100 ${form.triggerType === trigger.value
                                                    ? 'border-primary bg-primary bg-opacity-10'
                                                    : 'hover-shadow'
                                                    }`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => set('triggerType', trigger.value)}
                                            >
                                                <div className='fw-semibold'>{trigger.label}</div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Tab>
                            <Tab eventKey="policy" title="Policy / Quote">
                                <Row xs={1} sm={2} className='g-2'>
                                    {TRIGGER_OPTIONS.filter((t) => ['CONDITIONAL_POLICY'].includes(t.value)).map((trigger) => (
                                        <Col key={trigger.value}>
                                            <div
                                                className={`border rounded p-3 cursor-pointer h-100 ${form.triggerType === trigger.value
                                                    ? 'border-primary bg-primary bg-opacity-10'
                                                    : 'hover-shadow'
                                                    }`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => set('triggerType', trigger.value)}
                                            >
                                                <div className='fw-semibold'>{trigger.label}</div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Tab>
                        </Tabs>

                        {/* Config for CONDITIONAL_LEAD */}
                        {form.triggerType === 'CONDITIONAL_LEAD' && (
                            <div className='mt-4 p-4 border rounded bg-light'>
                                <h6 className='fw-semibold mb-3'>Lead Condition Settings</h6>
                                <Form.Group className='mb-3'>
                                    <Form.Label>Condition Rule</Form.Label>
                                    <Form.Select 
                                        value={form.triggerConfig?.condition_rule || ''}
                                        onChange={(e) => set('triggerConfig', { ...form.triggerConfig, condition_rule: e.target.value })}
                                    >
                                        <option value="">Select a rule...</option>
                                        {LEAD_CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </Form.Select>
                                </Form.Group>

                                {form.triggerConfig?.condition_rule === 'LEAD_DURATION' && (
                                    <Form.Group>
                                        <Form.Label>Duration (Days)</Form.Label>
                                        <Form.Control 
                                            type="number" 
                                            min="0"
                                            value={form.triggerConfig?.duration_days || 0}
                                            onChange={(e) => set('triggerConfig', { ...form.triggerConfig, duration_days: parseInt(e.target.value) || 0 })}
                                        />
                                    </Form.Group>
                                )}

                                {form.triggerConfig?.condition_rule === 'LEAD_STAGE' && (
                                    <Form.Group>
                                        <Form.Label>Lead Stage</Form.Label>
                                        <Form.Select
                                            value={form.triggerConfig?.lead_stage || ''}
                                            onChange={(e) => set('triggerConfig', { ...form.triggerConfig, lead_stage: e.target.value })}
                                        >
                                            <option value="">Select Stage...</option>
                                            <option value="NEW">New</option>
                                            <option value="SOLD">Sold</option>
                                            <option value="COMPLETED">Completed</option>
                                        </Form.Select>
                                    </Form.Group>
                                )}
                            </div>
                        )}

                        {/* Config for CONDITIONAL_POLICY */}
                        {form.triggerType === 'CONDITIONAL_POLICY' && (
                            <div className='mt-4 p-4 border rounded bg-light'>
                                <h6 className='fw-semibold mb-3'>Policy/Quote Condition Settings</h6>
                                <Form.Group className='mb-3'>
                                    <Form.Label>Condition Rule</Form.Label>
                                    <Form.Select 
                                        value={form.triggerConfig?.condition_rule || ''}
                                        onChange={(e) => set('triggerConfig', { ...form.triggerConfig, condition_rule: e.target.value })}
                                    >
                                        <option value="">Select a rule...</option>
                                        {POLICY_CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </Form.Select>
                                </Form.Group>

                                {(form.triggerConfig?.condition_rule === 'STATE_CHANGE' || 
                                  form.triggerConfig?.condition_rule === 'CROSS_SELL_AUTO' || 
                                  form.triggerConfig?.condition_rule === 'CROSS_SELL_HOME' || 
                                  form.triggerConfig?.condition_rule === 'POLICY_COMPLETED') && (
                                    <Form.Group className='mt-3'>
                                        <Form.Label>Duration in this state (Days)</Form.Label>
                                        <Form.Control 
                                            type="number" 
                                            min="0"
                                            value={form.triggerConfig?.duration_days || 0}
                                            onChange={(e) => set('triggerConfig', { ...form.triggerConfig, duration_days: parseInt(e.target.value) || 0 })}
                                        />
                                    </Form.Group>
                                )}

                                {form.triggerConfig?.condition_rule === 'BY_CARRIER' && (
                                    <Form.Group className='mt-3'>
                                        <Form.Label>Carrier Name or ID</Form.Label>
                                        <Form.Control 
                                            type="text" 
                                            placeholder="Enter Carrier..."
                                            value={form.triggerConfig?.carrier || ''}
                                            onChange={(e) => set('triggerConfig', { ...form.triggerConfig, carrier: e.target.value })}
                                        />
                                    </Form.Group>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* ─── Step 2: Canal + Template ─── */}
                {step === 2 && (
                    <div>
                        <h6 className='mb-3'>📨 Through which channel and with what template?</h6>

                        <Form.Group className='mb-4'>
                            <Form.Label className='fw-semibold'>Sending channel</Form.Label>
                            <div className='d-flex gap-3'>
                                {(['EMAIL', 'SMS'] as const).map((ch) => (
                                    <div
                                        key={ch}
                                        className={`border rounded px-4 py-3 text-center ${form.channel === ch
                                            ? 'border-primary bg-primary bg-opacity-10 fw-bold'
                                            : ''
                                            }`}
                                        style={{ cursor: 'pointer', minWidth: 120 }}
                                        onClick={() => { set('channel', ch); set('templateId', '') }}
                                    >
                                        {ch === 'EMAIL' ? '✉️ Email' : '💬 SMS'}
                                    </div>
                                ))}
                            </div>
                        </Form.Group>

                        <Form.Group>
                            <div className='d-flex justify-content-between align-items-center mb-1'>
                                <Form.Label className='fw-semibold mb-0'>Template</Form.Label>
                                <Button variant='link' size='sm' className='p-0 text-decoration-none' onClick={handleOpenCreateTemplate}>
                                    + Create Template
                                </Button>
                            </div>
                            {loadingTemplates ? (
                                <div className='text-muted'><Spinner animation='border' size='sm' /> Loading...</div>
                            ) : templates.length === 0 ? (
                                <div className='alert alert-warning small py-2'>
                                    No templates available for <strong>{form.channel}</strong>.
                                    Create one in the Templates module and return here.
                                </div>
                            ) : (
                                <Row xs={1} sm={2} className='g-2'>
                                    {templates.map((t) => (
                                        <Col key={t.id}>
                                            <div
                                                className={`border rounded p-3 h-100 ${form.templateId === t.id
                                                    ? 'border-primary bg-primary bg-opacity-10'
                                                    : ''
                                                    }`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => set('templateId', t.id)}
                                            >
                                                <div className='fw-semibold small'>{t.name}</div>
                                                {t.subject && (
                                                    <div className='text-muted' style={{ fontSize: 12 }}>
                                                        Subject: {t.subject}
                                                    </div>
                                                )}
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </Form.Group>
                    </div>
                )}

                {/* ─── Step 3: Configuración ─── */}
                {step === 3 && (
                    <div>
                        <h6 className='mb-3'>⚙️ Final configuration</h6>
                        <Row>
                            <Col md={8}>
                                <Form.Group className='mb-3'>
                                    <Form.Label className='fw-semibold'>Name *</Form.Label>
                                    <Form.Control
                                        value={form.name}
                                        onChange={(e) => set('name', e.target.value)}
                                        placeholder='Ex: 30 days expiration reminder'
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className='mb-3'>
                                    <Form.Label className='fw-semibold'>Status</Form.Label>
                                    <div className='d-flex align-items-center gap-2 mt-1'>
                                        <Form.Check
                                            type='switch'
                                            id='auto-active-switch'
                                            label={form.isActive ? 'Active' : 'Inactive'}
                                            checked={form.isActive}
                                            onChange={(e) => set('isActive', e.target.checked)}
                                        />
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <Form.Group className='mb-3'>
                                    <Form.Label className='fw-semibold'>Scope</Form.Label>
                                    <Form.Select
                                        value={form.scope}
                                        onChange={(e) => set('scope', e.target.value)}
                                    >
                                        <option value="PERSONAL">Personal (Assigned to me)</option>
                                        <option value="ORGANIZATION">Organization Wide</option>
                                    </Form.Select>
                                    <Form.Text className='text-muted'>
                                        Determine if this automation applies to all contacts in the organization or only those assigned to you.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className='mb-3'>
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as='textarea'
                                rows={2}
                                value={form.description}
                                onChange={(e) => set('description', e.target.value)}
                                placeholder='Optional description...'
                            />
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className='mb-3'>
                                    <Form.Label>⏳ Delay (days after trigger)</Form.Label>
                                    <Form.Control
                                        type='number'
                                        min={0}
                                        max={365}
                                        value={form.delayDays}
                                        onChange={(e) => set('delayDays', parseInt(e.target.value) || 0)}
                                    />
                                    <Form.Text>0 = send immediately</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className='mb-3'>
                                    <Form.Label>🕐 Preferred sending hour</Form.Label>
                                    <Form.Select
                                        value={form.sendHour}
                                        onChange={(e) => set('sendHour', parseInt(e.target.value))}
                                    >
                                        {Array.from({ length: 24 }, (_, i) => (
                                            <option key={i} value={i}>
                                                {String(i).padStart(2, '0')}:00
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Summary */}
                        <div className='alert alert-light border mt-2 small'>
                            <strong>📋 Summary:</strong>{' '}
                            Trigger <strong>{TRIGGER_OPTIONS.find(t => t.value === form.triggerType)?.label}</strong>{' '}
                            {(form.triggerType === 'CONDITIONAL_LEAD' || form.triggerType === 'CONDITIONAL_POLICY') && form.triggerConfig?.condition_rule && ` (${form.triggerConfig.condition_rule})`} →{' '}
                            <strong>{form.channel}</strong> →{' '}
                            Template <strong>{templates.find(t => t.id === form.templateId)?.name || 'unselected'}</strong>
                            {form.delayDays > 0 && ` → delay ${form.delayDays} day(s)`}
                        </div>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <div className='d-flex justify-content-between w-100'>
                    <div>
                        {step > 1 && (
                            <Button variant='outline-secondary' onClick={() => setStep(s => s - 1)}>
                                ← Back
                            </Button>
                        )}
                    </div>
                    <div className='d-flex gap-2'>
                        <Button variant='outline-secondary' onClick={onHide}>Cancel</Button>
                        {step < 3 ? (
                            <Button variant='primary' onClick={() => setStep(s => s + 1)}>
                                Next →
                            </Button>
                        ) : (
                            <Button
                                variant='success'
                                onClick={handleSave}
                                disabled={creating || updating}
                            >
                                {(creating || updating) && <Spinner animation='border' size='sm' className='me-1' />}
                                {automation ? 'Save changes' : 'Create automation'}
                            </Button>
                        )}
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
        
        {/* Template Creation Modals */}
        <TemplateStarterModal
            show={starterModalOpen}
            onHide={() => setStarterModalOpen(false)}
            onSelect={handleSelectStarter}
            onStartFromScratch={handleStartFromScratch}
        />
        <TemplateFormModal
            show={templateModalOpen}
            onHide={() => setTemplateModalOpen(false)}
            onSubmit={handleCreateTemplateSubmit}
            starter={selectedStarter}
            loading={creatingTemplate}
            defaultChannel={defaultChannel}
        />
        </>
    )
}
